/* eslint-disable @next/next/no-img-element */
import { CSSProperties, useRef, useState } from "react"
import Cursor from "public/marketing/glow-cursor.png"
import useIsMobile from "app/core/hooks/useIsMobile"

// function to 2 decimal places
const to2dp = (num) => Math.round((num + Number.EPSILON) * 100) / 100
// Q: Why must we add Number.EPSILON?
// A: https://stackoverflow.com/questions/11832914/round-to-at-most-2-decimal-places-only-if-necessary

type GlowStrength = "extra-light" | "light" | "medium" | "strong"

export const HoverSkew = ({
  glowStength = "medium",
  className,
  children,
  style = {},
  clickable = false,
  borderRadius = "calc(min(max(16px, 2vw), 40px))",
}: {
  glowStength?: GlowStrength
  className?: string
  style?: CSSProperties
  clickable?: boolean
  borderRadius?: string
  children
}) => {
  const [rotationValues, setRotationValues] = useState({ x: 0, y: 0 })
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [mouseRatioPosition, setMouseRatioPosition] = useState({ x: 0, y: 0 })
  const [shouldTransition, setShouldTransition] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [hoverScale, setHoverScale] = useState(1.0)
  const [didClick, setDidClick] = useState(false)

  const ref = useRef<HTMLInputElement>(null)

  const isMobile = useIsMobile()

  const onMouseMove = (e) => {
    if (ref.current) {
      if (clickable && didClick) {
        return
      }

      // Get the target
      const target = e.target

      // prevent propagation
      e.stopPropagation()

      // Get the bounding rectangle of target
      const rect = ref.current.getBoundingClientRect()

      // Mouse position
      const x = to2dp(e.clientX - rect.left)
      const y = to2dp(e.clientY - rect.top)

      const amount = 2.0

      const xRotate = to2dp(((x / rect.width) * 2 - 1) * -1 * amount)
      const yRotate = to2dp(((y / rect.height) * 2 - 1) * amount * 2)

      // Set the state

      if (xRotate != rotationValues.x || yRotate != rotationValues.y) {
        setRotationValues({ x: xRotate, y: yRotate })
      }

      if (x != mousePosition.x || y != mousePosition.y) {
        setMousePosition({ x, y })
      }

      const mrx = to2dp(x / rect.width)
      const mry = to2dp(y / rect.height)

      if (mrx != mouseRatioPosition.x || mry != mouseRatioPosition.y) {
        setMouseRatioPosition({ x: mrx, y: mry })
      }
    }
  }

  const glowpacities = {
    "extra-light": 0.08,
    light: 0.1,
    medium: 0.125,
    strong: 0.175,
  }

  const opacityAmount = glowpacities[glowStength]

  return (
    <div
      ref={ref}
      onMouseMove={onMouseMove}
      className={`relative bg-grayscale-lighter bg-opacity-10 border border-white border-1 border-opacity-10  ${
        clickable ? "hover:border-opacity-[0.15] hover:shadow-2xl" : "shadow-2xl"
      } ${className} ${
        shouldTransition
          ? "transition-all duration-[0.3s] ease-out"
          : "transition-border-opacity duration-[0.2s] ease-out"
      }`}
      onMouseEnter={(e) => {
        setIsHovering(true)
        setShouldTransition(true)
        setTimeout(() => {
          setShouldTransition(false)
        }, 300)
        onMouseMove(e)
        if (clickable) {
          setDidClick(false)
        }
      }}
      onMouseLeave={(e) => {
        setIsHovering(false)
        setShouldTransition(true)
        setRotationValues({ x: 0, y: 0 })
        setTimeout(() => {
          setShouldTransition(false)
        }, 300)
      }}
      onMouseDown={(e) => {
        if (!clickable) {
          return
        }
        setDidClick(true)
        setIsHovering(false)
        setShouldTransition(true)
        setRotationValues({ x: 0, y: 0 })
        setTimeout(() => {
          setShouldTransition(false)
        }, 300)
      }}
      style={{
        transform: isMobile
          ? ""
          : `perspective(900px)  rotateY(${to2dp(rotationValues.x)}deg) rotateX(${to2dp(
              rotationValues.y
            )}deg) scale(${isHovering ? 1.015 : 1})`,
        borderRadius: borderRadius,
        overflow: "hidden",
        ...(style || {}),
      }}
    >
      {!isMobile && (
        <img
          style={{
            pointerEvents: "none",
            zIndex: -1,
            left: mousePosition.x,
            top: mousePosition.y,
            transform: `translate(-50%, -50%) scale(${to2dp(0.75 + mouseRatioPosition.y)})`,
            opacity: isHovering ? opacityAmount : 0,
            display: "flex",
            justifyContent: "center",

            alignItems: "center",
          }}
          className={`absolute transform-gpu ${
            shouldTransition ? "transition-opacity duration-[0.3s] ease-out" : ""
          }`}
          src={Cursor.src}
          alt="dashboard-header"
        ></img>
      )}
      {children}
    </div>
  )
}
